import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import {
  useConfirmReceiveConvoyMutation,
  useGetReceiveConvoyContextQuery,
  type ReceiveConvoyConfirmResultDto,
  type ReceiveConvoyLocationDto
} from '@/entities/receive';
import {
  CONFIRM_ARRIVAL_CONFLICT_REASON,
  CONFIRM_ARRIVAL_UNIT_MODE,
  type ConfirmArrivalUnitMode
} from '@/features/receive-confirm-arrival';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

import { buildConvoyConfirmBody, buildConvoyFormDefaults } from '../lib/build-payload';
import { getReceiveConvoySchema } from './validation';
import type { ReceiveConvoyFormValues, ReceiveConvoyTarget } from './types';

interface UseReceiveConvoyFormArgs {
  target: ReceiveConvoyTarget;
  onClose: () => void;
  /** Fired after a successful receipt — the dashboard uses it to drop stale branches. */
  onSuccess?: (result: ReceiveConvoyConfirmResultDto) => void;
}

const EMPTY_LOCATIONS: ReceiveConvoyLocationDto[] = [];

export const useReceiveConvoyForm = ({ target, onClose, onSuccess }: UseReceiveConvoyFormArgs) => {
  const { t, i18n } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const [unitMode, setUnitMode] = useState<ConfirmArrivalUnitMode>(CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY);
  const [result, setResult] = useState<ReceiveConvoyConfirmResultDto | null>(null);

  const {
    data: context,
    isLoading,
    error: contextError,
    refetch
  } = useGetReceiveConvoyContextQuery(
    { convoyId: target.convoyId, projectId, siteId, lang: i18n.language },
    { skip: !projectId || !siteId }
  );

  const [confirmConvoy, { isLoading: isSubmitting }] = useConfirmReceiveConvoyMutation();

  const schema = useMemo(() => getReceiveConvoySchema(t, context), [t, context]);

  const form = useForm<ReceiveConvoyFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      actualDateOfReceipt: '',
      explanationOfLosses: '',
      printGoodsReceivedNote: false,
      shipments: []
    }
  });

  const { reset, setValue, getValues } = form;

  // Seeded once per context load. The receipt invalidates the `Receive` tag,
  // which refetches this very context — that refetch must not wipe the form
  // behind the result, so the reset is guarded rather than keyed on `context`.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!context || seededRef.current) return;
    seededRef.current = true;
    reset(buildConvoyFormDefaults(context));
  }, [context, reset]);

  const locationsById = useMemo(
    () => new Map((context?.receiveLocations ?? EMPTY_LOCATIONS).map((location) => [location.locationId, location])),
    [context?.receiveLocations]
  );

  /** Clears every quantity of every truck — the legacy "× reset quantities" control. */
  const handleResetQuantities = useCallback(() => {
    const shipments = getValues('shipments').map((shipment) => ({
      ...shipment,
      lines: shipment.lines.map((line) => ({ ...line, goodQty: 0, damagedQty: 0, lostQty: 0 }))
    }));
    setValue('shipments', shipments, { shouldDirty: true, shouldValidate: true });
  }, [getValues, setValue]);

  const submit = useCallback(
    async (values: ReceiveConvoyFormValues) => {
      if (!context || !projectId || !siteId) return;

      try {
        const confirmed = await confirmConvoy({
          convoyId: target.convoyId,
          projectId,
          siteId,
          lang: i18n.language,
          body: buildConvoyConfirmBody(values, context)
        }).unwrap();

        setResult(confirmed);
        onSuccess?.(confirmed);
        toast.success(
          t('receive.convoy.success', 'Convoy received: {{count}} receipt(s) recorded', {
            count: confirmed.receipts.length
          })
        );
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          t('receive.confirm.error.submitFailed', 'Failed to record the receipt. Please try again.')
        );
        toast.error(message);

        // All or nothing: a race on any truck books none of them. The only safe
        // move is to re-read the context and start over.
        if (message.includes(CONFIRM_ARRIVAL_CONFLICT_REASON.QUANTITY_CHANGED)) {
          seededRef.current = false;
          void refetch();
        }
      }
    },
    [context, projectId, siteId, confirmConvoy, target.convoyId, i18n.language, onSuccess, t, refetch]
  );

  return {
    form,
    context,
    locationsById,
    unitMode,
    setUnitMode,
    result,
    isLoading,
    isSubmitting,
    contextError,
    handleResetQuantities,
    handleSubmit: form.handleSubmit(submit),
    handleClose: onClose
  };
};
